/**
 * @description
 * Caching utility for performance optimization
 * Caches items to avoid repeated lookups
 *
 * @example
 * Usage:
 * const cache = new Cache()
 * cache.set('item:FUND', true)
 * const result = cache.get('item:FUND') // true
 * cache.invalidate('FUND') // Clear all keys containing 'FUND'
 * cache.clear()
 */

export class Cache {
	private cache = new Map<string, boolean>()
	private timestamps = new Map<string, number>()

	/**
	 * TTL (Time To Live) in milliseconds
	 * Default: 5 minutes
	 * After this time, cached values are considered stale
	 */
	private readonly TTL = 5 * 60 * 1000

	/**
	 * Get a cached permission check result
	 *
	 * @param key - Cache key (e.g., 'permission:Fund Business Wallet')
	 * @returns true/false if cached and valid, null if expired or not cached
	 */
	get(key: string): boolean | null {
		const timestamp = this.timestamps.get(key)

		// Check if entry exists
		if (timestamp === undefined) {
			return null
		}

		// Check if entry has expired
		if (Date.now() - timestamp > this.TTL) {
			// Clean up expired entry
			this.cache.delete(key)
			this.timestamps.delete(key)
			return null
		}

		// Return cached value
		return this.cache.get(key) ?? null
	}

	/**
	 * Set a cached permission check result
	 *
	 * @param key - Cache key (e.g., 'permission:Fund Business Wallet')
	 * @param value - The permission check result (true/false)
	 */
	set(key: string, value: boolean): void {
		this.cache.set(key, value)
		this.timestamps.set(key, Date.now())
	}

	/**
	 * Clear all cached entries
	 * Useful on logout or permission refresh
	 */
	clear(): void {
		this.cache.clear()
		this.timestamps.clear()
	}

	/**
	 * Invalidate cache entries matching a pattern
	 * Useful when permissions change
	 *
	 * @param pattern - Pattern to match (substring match)
	 * @example
	 * invalidate('FUND') // Clears all keys containing 'FUND'
	 * invalidate('permission:') // Clears all permission keys
	 */
	invalidate(pattern: string): void {
		const keysToDelete: string[] = []

		this.cache.forEach((_, key) => {
			if (key.includes(pattern)) {
				keysToDelete.push(key)
			}
		})

		keysToDelete.forEach(key => {
			this.cache.delete(key)
			this.timestamps.delete(key)
		})
	}

	/**
	 * Get cache statistics (for debugging)
	 *
	 * @returns Object with cache info
	 */
	getStats(): { size: number; keys: string[] } {
		return {
			size: this.cache.size,
			keys: Array.from(this.cache.keys())
		}
	}

	/**
	 * Get remaining TTL for a key (in milliseconds)
	 *
	 * @param key - Cache key
	 * @returns Remaining TTL in ms, or -1 if not cached/expired
	 */
	getRemaining(key: string): number {
		const timestamp = this.timestamps.get(key)

		if (timestamp === undefined) {
			return -1
		}

		const remaining = this.TTL - (Date.now() - timestamp)
		return remaining > 0 ? remaining : -1
	}

	/**
	 * Check if a key exists in cache
	 *
	 * @param key - Cache key
	 * @returns true if key exists and is valid
	 */
	has(key: string): boolean {
		return this.get(key) !== null
	}

	/**
	 * Get all valid cache entries
	 *
	 * @returns Map of all valid cached entries
	 */
	getAll(): Map<string, boolean> {
		const valid = new Map<string, boolean>()

		this.cache.forEach((value, key) => {
			const timestamp = this.timestamps.get(key)

			if (timestamp !== undefined && Date.now() - timestamp <= this.TTL) {
				valid.set(key, value)
			}
		})

		return valid
	}

	/**
	 * Set custom TTL
	 * WARNING: Modifies prototype, affects all instances
	 *
	 * @param ttlMs - TTL in milliseconds
	 */
	setTTL(ttlMs: number): void {
		Object.defineProperty(this, 'TTL', {
			value: ttlMs,
			writable: false
		})
	}
}

/**
 * Global cache instance
 */
export const customCache = new Cache()

/**
 * Helper function to generate cache keys
 */
export function generateCacheKey(type: string, value: string | string[]): string {
	if (Array.isArray(value)) {
		return `${type}:${value.join(',')}`
	}
	return `${type}:${value}`
}
